/**
 * useEmbedConfig — fetch the org's shared embed defaults from
 * /api/embed/config exactly once per page load, per host (cached
 * module-globally so multiple surface mounts don't double-fetch).
 *
 * Returns `undefined` while loading. Surfaces merge this with explicit
 * props (props win over server config; server config wins over built-in
 * defaults).
 *
 * Per plan-per-host-starter-prompts.md: starter prompts are per-host,
 * so the cache is keyed by hostApp. The server resolves the
 * hostApp → prompts mapping; the embed sees a flat array.
 */

import { useEffect, useState } from 'react'

import { resolveApiBase } from '../lib/socket-client'

export interface EmbedConfig {
  disabled: boolean
  crossAppSearch: boolean
  voiceInput: boolean
  suggestions: string[]
}

const cache = new Map<string, EmbedConfig | null>()
const inflight = new Map<string, Promise<EmbedConfig | null>>()

async function fetchConfig(baseUrl: string, hostApp: string): Promise<EmbedConfig | null> {
  try {
    const url = `${baseUrl}/api/embed/config${hostApp ? `?hostApp=${encodeURIComponent(hostApp)}` : ''}`
    const res = await fetch(url, { credentials: 'include' })
    if (!res.ok) {
      return null
    }
    const body = (await res.json()) as Partial<EmbedConfig>
    if (body.disabled) {
      return {
        disabled: true,
        crossAppSearch: false,
        voiceInput: false,
        suggestions: [],
      }
    }
    return {
      disabled: false,
      crossAppSearch: body.crossAppSearch ?? true,
      voiceInput: body.voiceInput ?? true,
      suggestions: body.suggestions ?? [],
    }
  } catch {
    return null
  }
}

export function useEmbedConfig(hostApp: string, apiBaseUrl?: string): EmbedConfig | undefined {
  const cached = cache.get(hostApp)
  const [config, setConfig] = useState<EmbedConfig | undefined>(cached ?? undefined)

  useEffect(() => {
    const existing = cache.get(hostApp)
    if (existing) {
      setConfig(existing)
      return
    }
    const base = resolveApiBase(apiBaseUrl ?? '')
    let promise = inflight.get(hostApp)
    if (!promise) {
      promise = fetchConfig(base, hostApp).then((c) => {
        cache.set(hostApp, c)
        inflight.delete(hostApp)
        return c
      })
      inflight.set(hostApp, promise)
    }
    let cancelled = false
    void promise.then((c) => {
      if (!cancelled && c) {
        setConfig(c)
      }
    })
    return () => {
      cancelled = true
    }
  }, [hostApp, apiBaseUrl])

  return config
}

/**
 * Test helper — clears the module-level cache so repeated mounts in unit
 * tests can each see a fresh fetch. Not part of the public API.
 */
export function __resetEmbedConfigCache(): void {
  cache.clear()
  inflight.clear()
}
